import { httpsCallable } from "firebase/functions";

import { toAppError } from "@/lib/errors";
import { functions } from "@/lib/firebase";
import type {
  CheckSlugReq,
  CheckSlugResp,
  CreateSiteReq,
  CreateSiteResp,
  DeleteSiteReq,
  DeleteSiteResp,
  GenerateColorSchemeReq,
  GenerateColorSchemeResp,
  GenerateFontSchemeReq,
  GenerateFontSchemeResp,
  GenerateThemeReq,
  GenerateThemeResp,
  GetSiteReq,
  GetSiteResp,
  GetDjAccessStatusReq,
  GetDjAccessStatusResp,
  GetTextOverrideReq,
  GetTextOverrideResp,
  GetTranslationsReq,
  GetTranslationsResp,
  ListSitesResp,
  ListGoogleFontsResp,
  ListUsersResp,
  ListVariantsResp,
  SaveTextOverrideReq,
  SaveTextOverrideResp,
  SaveTranslationsReq,
  SaveTranslationsResp,
  SetDjAccessCodeReq,
  SetDjAccessCodeResp,
  UpdateSiteConfigReq,
  UpdateSiteConfigResp,
  UpdateSiteReq,
  UpdateSiteResp,
  ListTemplatesResp,
  GetTemplateReq,
  GetTemplateResp,
  SaveTemplateReq,
  SaveTemplateResp,
  DeleteTemplateReq,
  DeleteTemplateResp,
  SetDefaultTemplateReq,
  SetDefaultTemplateResp,
  GetPricingDraftResp,
  SavePricingDraftReq,
  SavePricingDraftResp,
  PublishPricingReq,
  PublishPricingResp,
  GetMySetupReq,
  GetMySetupResp,
  CreateMySiteReq,
  CreateMySiteResp,
  UpdateMySiteReq,
  UpdateMySiteResp,
  UpdateMySiteConfigReq,
  UpdateMySiteConfigResp,
  CheckMySlugReq,
  CheckMySlugResp,
  SetMyDjPinReq,
  SetMyDjPinResp,
  GenerateMyThemeReq,
  GenerateMyThemeResp,
  GetBillingConfigResp,
  SetBillingDefaultsReq,
  SetBillingDefaultsResp,
  CreateOrderReq,
  CreateOrderResp,
  GetMyOrderReq,
  GetMyOrderResp,
  GetMyOrderInvoiceReq,
  GetMyOrderInvoiceResp,
  ListMyOrdersResp,
  ListOrdersReq,
  ListOrdersResp,
  ZipResp,
} from "@/lib/types";

async function callAdmin<TReq, TResp>(name: string, data: TReq): Promise<TResp> {
  try {
    const timeout = CALLABLE_TIMEOUTS_MS[name] ?? DEFAULT_TIMEOUT_MS;
    const fn = httpsCallable<TReq, TResp>(functions, name, { timeout });
    const result = await fn(data);
    return result.data;
  } catch (err) {
    throw toAppError(err);
  }
}

/** Read-only / fast admin calls. Firebase's default is 70 s; we cap lower. */
const DEFAULT_TIMEOUT_MS = 30_000;

/**
 * Per-callable timeout overrides. The Firebase SDK defaults to 70 s on the
 * site — anything longer-running on the server needs an explicit bump or
 * the site gives up while the function keeps working.
 *
 *  - `createSite` / `deleteSite`: write Firestore + purge Storage
 *    (fast now that nothing is published externally).
 */
const CALLABLE_TIMEOUTS_MS: Record<string, number> = {
  createSite: 180_000,
  deleteSite: 180_000,
  generateColorScheme: 60_000,
  generateFontScheme: 60_000,
  generateTheme: 60_000,
  generateMyTheme: 60_000,
  // Calls out to the invoice system for the payment QR.
  createOrder: 60_000,
  createMySite: 60_000,
};

export const listSites = (): Promise<ListSitesResp> =>
  callAdmin<undefined, ListSitesResp>("listSites", undefined);

export const listUsers = (): Promise<ListUsersResp> =>
  callAdmin<undefined, ListUsersResp>("listUsers", undefined);

export const getSite = (i: GetSiteReq): Promise<GetSiteResp> =>
  callAdmin<GetSiteReq, GetSiteResp>("getSite", i);

export const createSite = (i: CreateSiteReq): Promise<CreateSiteResp> =>
  callAdmin<CreateSiteReq, CreateSiteResp>("createSite", i);

export const checkSlug = (i: CheckSlugReq): Promise<CheckSlugResp> =>
  callAdmin<CheckSlugReq, CheckSlugResp>("checkSlug", i);

export const updateSite = (i: UpdateSiteReq): Promise<UpdateSiteResp> =>
  callAdmin<UpdateSiteReq, UpdateSiteResp>("updateSite", i);

export const updateSiteConfig = (i: UpdateSiteConfigReq): Promise<UpdateSiteConfigResp> =>
  callAdmin<UpdateSiteConfigReq, UpdateSiteConfigResp>("updateSiteConfig", i);

export const generateColorScheme = (
  i: GenerateColorSchemeReq,
): Promise<GenerateColorSchemeResp> =>
  callAdmin<GenerateColorSchemeReq, GenerateColorSchemeResp>("generateColorScheme", i);

export const listGoogleFonts = (): Promise<ListGoogleFontsResp> =>
  callAdmin<undefined, ListGoogleFontsResp>("listGoogleFonts", undefined);

export const generateFontScheme = (
  i: GenerateFontSchemeReq,
): Promise<GenerateFontSchemeResp> =>
  callAdmin<GenerateFontSchemeReq, GenerateFontSchemeResp>("generateFontScheme", i);

export const generateTheme = (
  i: GenerateThemeReq,
): Promise<GenerateThemeResp> =>
  callAdmin<GenerateThemeReq, GenerateThemeResp>("generateTheme", i);

export const deleteSite = (i: DeleteSiteReq): Promise<DeleteSiteResp> =>
  callAdmin<DeleteSiteReq, DeleteSiteResp>("deleteSite", i);

export const createZip = (i: { siteId: string }): Promise<ZipResp> =>
  callAdmin<{ siteId: string }, ZipResp>("createZip", i);

export const findZip = (i: { siteId: string }): Promise<ZipResp> =>
  callAdmin<{ siteId: string }, ZipResp>("findZip", i);

export const setDjAccessCode = (i: SetDjAccessCodeReq): Promise<SetDjAccessCodeResp> =>
  callAdmin<SetDjAccessCodeReq, SetDjAccessCodeResp>("setDjAccessCode", i);

export const getDjAccessStatus = (i: GetDjAccessStatusReq): Promise<GetDjAccessStatusResp> =>
  callAdmin<GetDjAccessStatusReq, GetDjAccessStatusResp>("getDjAccessStatus", i);

// --- Translations ---

export const listVariants = (): Promise<ListVariantsResp> =>
  callAdmin<undefined, ListVariantsResp>("listVariants", undefined);

export const getTranslations = (i: GetTranslationsReq): Promise<GetTranslationsResp> =>
  callAdmin<GetTranslationsReq, GetTranslationsResp>("getTranslations", i);

export const saveTranslations = (i: SaveTranslationsReq): Promise<SaveTranslationsResp> =>
  callAdmin<SaveTranslationsReq, SaveTranslationsResp>("saveTranslations", i);

export const getTextOverride = (i: GetTextOverrideReq): Promise<GetTextOverrideResp> =>
  callAdmin<GetTextOverrideReq, GetTextOverrideResp>("getTextOverride", i);

export const saveTextOverride = (i: SaveTextOverrideReq): Promise<SaveTextOverrideResp> =>
  callAdmin<SaveTextOverrideReq, SaveTextOverrideResp>("saveTextOverride", i);

// --- Templates ---

export const listTemplates = (): Promise<ListTemplatesResp> =>
  callAdmin<undefined, ListTemplatesResp>("listTemplates", undefined);

export const getTemplate = (i: GetTemplateReq): Promise<GetTemplateResp> =>
  callAdmin<GetTemplateReq, GetTemplateResp>("getTemplate", i);

export const saveTemplate = (i: SaveTemplateReq): Promise<SaveTemplateResp> =>
  callAdmin<SaveTemplateReq, SaveTemplateResp>("saveTemplate", i);

export const deleteTemplate = (i: DeleteTemplateReq): Promise<DeleteTemplateResp> =>
  callAdmin<DeleteTemplateReq, DeleteTemplateResp>("deleteTemplate", i);

export const setDefaultTemplate = (
  i: SetDefaultTemplateReq,
): Promise<SetDefaultTemplateResp> =>
  callAdmin<SetDefaultTemplateReq, SetDefaultTemplateResp>(
    "setDefaultTemplate",
    i,
  );

// --- Pricing ---

export const getPricingDraft = (): Promise<GetPricingDraftResp> =>
  callAdmin<undefined, GetPricingDraftResp>("getPricingDraft", undefined);

export const savePricingDraft = (i: SavePricingDraftReq): Promise<SavePricingDraftResp> =>
  callAdmin<SavePricingDraftReq, SavePricingDraftResp>("savePricingDraft", i);

export const publishPricing = (i: PublishPricingReq): Promise<PublishPricingResp> =>
  callAdmin<PublishPricingReq, PublishPricingResp>("publishPricing", i);

// --- Buyer site setup (post-payment wizard; signed-in, not admin) ---

export const getMySetup = (i: GetMySetupReq): Promise<GetMySetupResp> =>
  callAdmin<GetMySetupReq, GetMySetupResp>("getMySetup", i);

export const createMySite = (i: CreateMySiteReq): Promise<CreateMySiteResp> =>
  callAdmin<CreateMySiteReq, CreateMySiteResp>("createMySite", i);

export const updateMySite = (i: UpdateMySiteReq): Promise<UpdateMySiteResp> =>
  callAdmin<UpdateMySiteReq, UpdateMySiteResp>("updateMySite", i);

export const updateMySiteConfig = (
  i: UpdateMySiteConfigReq,
): Promise<UpdateMySiteConfigResp> =>
  callAdmin<UpdateMySiteConfigReq, UpdateMySiteConfigResp>(
    "updateMySiteConfig",
    i,
  );

export const checkMySlug = (i: CheckMySlugReq): Promise<CheckMySlugResp> =>
  callAdmin<CheckMySlugReq, CheckMySlugResp>("checkMySlug", i);

export const setMyDjPin = (i: SetMyDjPinReq): Promise<SetMyDjPinResp> =>
  callAdmin<SetMyDjPinReq, SetMyDjPinResp>("setMyDjPin", i);

export const generateMyTheme = (
  i: GenerateMyThemeReq,
): Promise<GenerateMyThemeResp> =>
  callAdmin<GenerateMyThemeReq, GenerateMyThemeResp>("generateMyTheme", i);

// --- Billing (invoice supplier defaults; backend proxies invoice-be) ---

export const getBillingConfig = (): Promise<GetBillingConfigResp> =>
  callAdmin<undefined, GetBillingConfigResp>("getBillingConfig", undefined);

export const setBillingDefaults = (
  i: SetBillingDefaultsReq,
): Promise<SetBillingDefaultsResp> =>
  callAdmin<SetBillingDefaultsReq, SetBillingDefaultsResp>(
    "setBillingDefaults",
    i,
  );

// --- Orders (buy flow; requires auth + verified email, not admin) ---

export const createOrder = (i: CreateOrderReq): Promise<CreateOrderResp> =>
  callAdmin<CreateOrderReq, CreateOrderResp>("createOrder", i);

export const getMyOrder = (i: GetMyOrderReq): Promise<GetMyOrderResp> =>
  callAdmin<GetMyOrderReq, GetMyOrderResp>("getMyOrder", i);

export const getMyOrderInvoice = (
  i: GetMyOrderInvoiceReq,
): Promise<GetMyOrderInvoiceResp> =>
  callAdmin<GetMyOrderInvoiceReq, GetMyOrderInvoiceResp>("getMyOrderInvoice", i);

export const listMyOrders = (): Promise<ListMyOrdersResp> =>
  callAdmin<undefined, ListMyOrdersResp>("listMyOrders", undefined);

export const listOrders = (i: ListOrdersReq): Promise<ListOrdersResp> =>
  callAdmin<ListOrdersReq, ListOrdersResp>("listOrders", i);
